/*@flow*/
import L from 'leaflet';
import tileJSON from 'lib/leaflet-plugins/TileJSONLayer';
import unique from 'lib/unique';

type OptionsT = {
  user_name?: string,
  userName?: string,
  dataLayerIds?: number[],
  sql: string,
};

type SubdomainsT = [string, string, string, string];
type CDNUrlT = {
  http: string,
  https: string,
  templates: {
    https: { subdomains: SubdomainsT, url: string },
  },
};
type UrlConfigT = {
  urlTemplate: string,
  subdomains: SubdomainsT,
};
type TileJSONConfigT = {
  tilejson: TileJSONVersionT,
  tiles: TileUrlsT,
};
type TileJSONVersionT = string;
type TileUrlsT = [string, string, string, string];
type MetadataT = {
  tilejson: {
    raster: TileJSONConfigT,
    vector: TileJSONConfigT,
  },
  url: {
    raster: UrlConfigT,
    vector: UrlConfigT,
  },
};
type MapApiResponseT = {
  layergroupid: string,
  cdn_url: CDNUrlT,
  metadata: MetadataT,
};
type ConfigT = { tiles: string[], grids: string[] };

const PATHNAME = 'api/v1/map';

const getMapAPIUrl = ({
  userName,
  apiKey,
}: {
  userName: string,
  apiKey: ?string,
}) => {
  const query = apiKey ? `?map_key=${apiKey}` : '';
  return `https://${userName}.cartodb.com:443/${PATHNAME}${query}`;
};

const CartoTile = L.FeatureGroup.extend({
  layerToken: null,
  interactivity: 'cartodb_id',
  metadata: null,
  name: 'CartoTile',

  options: {
    active: true,
    map_key: null,
    userName: 'swc28',
    apiKey: null,
    version: '1.2.0',
    dataLayerIds: [0],
    tiles: [],
    grids: [],
    // Grid options
    minzoom: null,
    minZoom: null,
    maxzoom: null,
    maxZoom: null,
    attribution: null,
    bounds: null,
    scheme: null,
  },

  initialize(options: OptionsT) {
    L.setOptions(this, options);
    if (!options.userName && options.user_name) {
      this.options.userName = options.userName;
    }
    return L.LayerGroup.prototype.initialize.call(this, [], options);
  },

  onAdd(map) {
    L.LayerGroup.prototype.onAdd.call(this, map);
    this.loadLayers(this.options);
  },

  loadLayers(options: OptionsT) {
    L.setOptions(this, options);
    this.invalidate();
    if (this.options.active) {
      this.getTiles().then(
        (c) => this.createLayers(c),
        (e) => console.error(e.stack)
      );
    }
  },

  getTiles(): Promise<ConfigT> {
    return new Promise((resolve, reject) => {
      if (this.metadata) {
        resolve(this.getConfig());
      } else {
        this.fetchMapConfig().then((data: MapApiResponseT) => {
          this.metadata = data.metadata;
          resolve(this.getConfig());
        }, reject);
      }
    });
  },

  createLayers(config: ConfigT) {
    L.setOptions(this, config);
    L.LayerGroup.prototype.clearLayers.call(this);
    this.createTileLayer();
  },

  createTileLayer() {
    this.addLayer(tileJSON({}, this.options));
  },

  fetchMapConfig(): Promise<MapApiResponseT> {
    let url = getMapAPIUrl({
      userName: this.options.userName,
      apiKey: this.getApiKey(),
    });

    return fetch(url, {
      method: 'post',
      body: JSON.stringify(this.toJSON()),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    }).then((res) => res.json());
  },

  getApiKey(): ?string {
    let { map_key, apiKey, api_key } = this.options;
    return map_key || api_key || apiKey;
  },

  getConfig(): ConfigT {
    if (!this.metadata) return {};
    const tiles = this.metadata.tilejson.raster.tiles;
    return { tiles, grids: [] };
  },

  invalidate() {
    this.metadata = null;
    L.LayerGroup.prototype.clearLayers.call(this);
  },

  toJSON() {
    let obj = {};
    obj.version = this.version;
    obj.layers = [];

    let { sql, interactivity, cartocss } = this.options;

    if (interactivity) {
      const cols = unique([this.interactivity, interactivity]);
      interactivity = cols.join(',');
    } else {
      interactivity = this.interactivity;
    }

    obj.layers.push({
      type: 'cartodb',
      options: {
        sql,
        cartocss,
        cartocss_version: '2.3.0',
        interactivity,
      },
    });

    return obj;
  },

  setOpacity(opacity) {
    this.eachLayer((l) => {
      if (typeof l.setOpacity === 'function') {
        l.setOpacity(opacity);
      }
    });
  },
});

export default (options: OptionsT): any => new CartoTile(options);
